/** Browser-safe product tracking contract. Page owners declare identifiers; features never name events. */
import * as z from 'zod';
export const UI_EVENT_NAMES = [
    'screen_viewed',
    'action_requested',
    'action_completed',
    'action_failed',
    'action_cancelled',
    'action_blocked',
];
export const UI_ACTION_COMPLETIONS = ['local_transition', 'server_acknowledgement', 'handoff_initiated'];
const identifier = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/);
const resourceId = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9][A-Za-z0-9:_-]*$/);
export const uiErrorCodeSchema = z.enum([
    'REQUEST_FAILED',
    'AUTH_REJECTED',
    'VALIDATION_REFUSED',
    'NETWORK_FAILED',
    'UNEXPECTED_FAILURE',
]);
export const uiCancellationReasonSchema = z.enum([
    'validation',
    'permission',
    'navigation',
    'scope-change',
    'user',
    'aborted',
    'not-ready',
    'not-found',
    'invalid-data',
    'unsupported-version',
    'not-allowed',
    'network',
    'conflict',
    'unexpected',
]);
export const uiEventSchema = z
    .strictObject({
    schemaVersion: z.literal(1),
    event: z.enum(UI_EVENT_NAMES),
    productSurface: z.enum(['web', 'shopify']),
    pageId: identifier,
    screenId: identifier,
    route: z
        .string()
        .min(1)
        .max(256)
        .regex(/^\/[A-Za-z0-9/_.:*-]*$/),
    viewId: z.uuid(),
    eventId: z.uuid(),
    journeyId: z.uuid().optional(),
    actionId: identifier.optional(),
    attemptId: z.uuid().optional(),
    completion: z.enum(UI_ACTION_COMPLETIONS).optional(),
    durationMs: z.number().int().min(0).max(86_400_000).optional(),
    httpStatus: z.number().int().min(100).max(599).optional(),
    errorCode: uiErrorCodeSchema.optional(),
    errorName: z
        .enum([
        'Error',
        'TypeError',
        'AbortError',
        'ValidationError',
        'PermissionError',
        'ConflictError',
        'NetworkError',
        'HttpError',
        'UnexpectedError',
    ])
        .optional(),
    reason: uiCancellationReasonSchema.optional(),
    setupId: resourceId.optional(),
    draftId: resourceId.optional(),
    studyId: resourceId.optional(),
    runId: resourceId.optional(),
})
    .superRefine((event, context) => {
    if (event.event === 'screen_viewed') {
        for (const key of [
            'actionId',
            'attemptId',
            'completion',
            'durationMs',
            'httpStatus',
            'errorCode',
            'errorName',
            'reason',
        ]) {
            if (event[key] !== undefined)
                context.addIssue({ code: 'custom', path: [key], message: 'Views cannot contain action outcomes' });
        }
        return;
    }
    if (!event.actionId || !event.attemptId || !event.completion) {
        context.addIssue({ code: 'custom', message: 'Actions require declared action, attempt and completion semantics' });
    }
    if (event.event === 'action_failed' && !event.errorCode) {
        context.addIssue({ code: 'custom', path: ['errorCode'], message: 'Failures require a safe error code' });
    }
    if ((event.event === 'action_cancelled' || event.event === 'action_blocked') && !event.reason) {
        context.addIssue({
            code: 'custom',
            path: ['reason'],
            message: 'Cancellation and refusal require a declared reason',
        });
    }
    if (event.event !== 'action_failed' && (event.errorCode || event.errorName || event.httpStatus)) {
        context.addIssue({ code: 'custom', message: 'Only failures contain error metadata' });
    }
    if (event.event !== 'action_cancelled' && event.event !== 'action_blocked' && event.reason) {
        context.addIssue({ code: 'custom', path: ['reason'], message: 'Only cancellation and refusal contain a reason' });
    }
});
/** Validate page ownership as well as shape; identifier-looking text alone is not a contract. */
export function parseUiEvent(input, pages) {
    const event = uiEventSchema.parse(input);
    const page = pages.find((candidate) => candidate.pageId === event.pageId);
    if (!page)
        throw new Error('UI_EVENT_UNKNOWN_PAGE');
    if (event.route !== page.route || !page.screens.includes(event.screenId))
        throw new Error('UI_EVENT_UNKNOWN_SCREEN');
    if (event.actionId &&
        (!Object.hasOwn(page.actions, event.actionId) || page.actions[event.actionId] !== event.completion)) {
        throw new Error('UI_EVENT_UNKNOWN_ACTION');
    }
    return event;
}
//# sourceMappingURL=ui-events.js.map